import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import type { AgentMessageResponse } from '../types/agent';
import {
  APPLY_ENV_ALL_KEYS,
  APPLY_ENV_SCOPE_ALL,
  agentFormIdsMatch,
  applyEnvCollectionLabel,
  applyEnvRequestLabel,
  buildApplyEnvToRequestPayload,
  buildApplyEnvToRequestUserMessage,
  fetchEnvironmentVariableKeys,
  normalizeAgentFormId,
  parseApplyEnvScopeSelectValue,
  resolveApplyEnvKeySelection,
  type ApplyEnvScopeId,
} from '../utils/agentApplyEnvUtils';
import { notifyAgentSideEffectsFromAgent } from '../utils/environmentVariablesEvents';
import {
  fetchRequestsForAgentCollectionScope,
  mapCollectionsForAgentMenus,
  type AgentCollectionOption,
} from '../utils/agentCollectionScope';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { AgentInlineSelect } from './ai-agent-ui';

const fieldGapSx = { mb: 1 };

export type AgentWidgetApplyEnvToRequestFormProps = {
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  defaultCollectionId?: number | '';
  defaultRequestId?: number | '';
  defaultEnvironmentId?: number | '';
  defaultKey?: string;
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetApplyEnvToRequestForm: React.FC<AgentWidgetApplyEnvToRequestFormProps> = ({
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  defaultCollectionId = '',
  defaultRequestId = '',
  defaultEnvironmentId = '',
  defaultKey = APPLY_ENV_ALL_KEYS,
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const { refreshProjects, setSelectedProject } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [collections, setCollections] = useState<AgentCollectionOption[]>([]);
  const [requests, setRequests] = useState<Array<{ id: number; name: string }>>([]);
  const [environments, setEnvironments] = useState<Array<{ id: number; name: string }>>([]);
  const [variableKeys, setVariableKeys] = useState<string[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<ApplyEnvScopeId | ''>('');
  const [selectedRequestId, setSelectedRequestId] = useState<ApplyEnvScopeId | ''>('');
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number | ''>('');
  const [selectedKey, setSelectedKey] = useState(defaultKey || APPLY_ENV_ALL_KEYS);
  const [submitting, setSubmitting] = useState(false);

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      if (defaultProjectId === '' || Number.isNaN(Number(defaultProjectId))) {
        return list;
      }
      const id = Number(defaultProjectId);
      if (list.some((p) => p.id === id)) return list;
      const name = defaultProjectName.trim() || `Project ${id}`;
      return [...list, { id, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      await refreshProjects();
      const fetched = await apiService.getProjects();
      setAvailableProjects(mergeProjectOption(fetched.map((p) => ({ id: p.id, name: p.name }))));
    } catch {
      setAvailableProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [mergeProjectOption, refreshProjects]);

  const loadKeys = useCallback(async (environmentId: number | '', preferredKey?: string) => {
    if (environmentId === '') {
      setVariableKeys([]);
      setSelectedKey(APPLY_ENV_ALL_KEYS);
      return;
    }
    setLoadingKeys(true);
    try {
      const keys = await fetchEnvironmentVariableKeys(Number(environmentId));
      setVariableKeys(keys);
      const preferred = preferredKey?.trim() ?? '';
      if (preferred && preferred !== APPLY_ENV_ALL_KEYS) {
        const match = keys.find((k) => k.toLowerCase() === preferred.toLowerCase());
        setSelectedKey(match ?? APPLY_ENV_ALL_KEYS);
      } else {
        setSelectedKey(APPLY_ENV_ALL_KEYS);
      }
    } catch {
      setVariableKeys([]);
      setSelectedKey(APPLY_ENV_ALL_KEYS);
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  const loadRequestsForScope = useCallback(
    async (
      projectId: number | '',
      collectionId: ApplyEnvScopeId | '',
      collectionRows: AgentCollectionOption[],
    ) => {
      if (projectId === '' || collectionId === '') {
        setRequests([]);
        setSelectedRequestId('');
        return [];
      }
      try {
        const mapped = await fetchRequestsForAgentCollectionScope(
          Number(projectId),
          collectionId,
          collectionRows,
        );
        const options = mapped.map((r) => ({ id: r.id, name: r.name }));
        setRequests(options);
        return options;
      } catch {
        setRequests([]);
        return [];
      }
    },
    [],
  );

  const loadProjectDetails = useCallback(
    async (projectId: number | '') => {
      if (projectId === '') {
        setCollections([]);
        setRequests([]);
        setEnvironments([]);
        setVariableKeys([]);
        setSelectedCollectionId('');
        setSelectedRequestId('');
        setSelectedEnvironmentId('');
        setSelectedKey(APPLY_ENV_ALL_KEYS);
        return;
      }
      setLoadingDetails(true);
      try {
        const [collectionsResult, environmentsResult] = await Promise.allSettled([
          apiService.getCollectionsByProject(Number(projectId)),
          apiService.getEnvironmentsByProject(Number(projectId)),
        ]);
        const colls = collectionsResult.status === 'fulfilled' ? collectionsResult.value : [];
        const envs = environmentsResult.status === 'fulfilled' ? environmentsResult.value : [];
        const mappedColls = mapCollectionsForAgentMenus(colls);
        const mappedEnvs = envs
          .map((e) => {
            const id = Number(e.id);
            if (!Number.isFinite(id) || id <= 0) return null;
            return { id, name: e.name };
          })
          .filter((e): e is { id: number; name: string } => e != null);
        setCollections(mappedColls);
        setEnvironments(mappedEnvs);

        const preferredCollectionId = normalizeAgentFormId(defaultCollectionId);
        let collectionId: ApplyEnvScopeId | '' =
          preferredCollectionId !== '' && mappedColls.some((c) => agentFormIdsMatch(c.id, preferredCollectionId))
            ? preferredCollectionId
            : APPLY_ENV_SCOPE_ALL;
        setSelectedCollectionId(collectionId);

        const reqs = await loadRequestsForScope(projectId, collectionId, mappedColls);

        const preferredRequestId = normalizeAgentFormId(defaultRequestId);
        let requestId: ApplyEnvScopeId | '' =
          preferredRequestId !== '' && reqs.some((r) => agentFormIdsMatch(r.id, preferredRequestId))
            ? preferredRequestId
            : APPLY_ENV_SCOPE_ALL;
        setSelectedRequestId(requestId);

        const preferredEnvironmentId = normalizeAgentFormId(defaultEnvironmentId);
        let environmentId: number | '' =
          preferredEnvironmentId !== '' && mappedEnvs.some((e) => agentFormIdsMatch(e.id, preferredEnvironmentId))
            ? preferredEnvironmentId
            : mappedEnvs.length > 0
              ? mappedEnvs[0].id
              : '';
        setSelectedEnvironmentId(environmentId);
        await loadKeys(environmentId, defaultKey);
      } catch {
        setCollections([]);
        setRequests([]);
        setEnvironments([]);
        setVariableKeys([]);
      } finally {
        setLoadingDetails(false);
      }
    },
    [defaultCollectionId, defaultEnvironmentId, defaultKey, defaultRequestId, loadKeys, loadRequestsForScope],
  );

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    const preferredProjectId = normalizeAgentFormId(defaultProjectId);
    if (
      preferredProjectId !== '' &&
      availableProjects.some((p) => agentFormIdsMatch(p.id, preferredProjectId))
    ) {
      setSelectedProjectId(preferredProjectId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  useEffect(() => {
    if (selectedProjectId === '') return;
    void loadProjectDetails(selectedProjectId);
  }, [selectedProjectId, loadProjectDetails]);

  useEffect(() => {
    if (selectedEnvironmentId === '') return;
    void loadKeys(selectedEnvironmentId, defaultKey);
  }, [selectedEnvironmentId, loadKeys, defaultKey]);

  const handleCollectionChange = async (collectionId: ApplyEnvScopeId | '') => {
    setSelectedCollectionId(collectionId);
    setSelectedRequestId(APPLY_ENV_SCOPE_ALL);
    await loadRequestsForScope(selectedProjectId, collectionId, collections);
    setSelectedRequestId(APPLY_ENV_SCOPE_ALL);
  };

  const handleApply = async () => {
    if (!selectedEnvironmentId || selectedCollectionId === '' || selectedRequestId === '') return;
    const envName =
      environments.find((e) => e.id === selectedEnvironmentId)?.name ??
      `environment #${selectedEnvironmentId}`;
    const collectionLabel = applyEnvCollectionLabel(selectedCollectionId, collections);
    const requestLabel = applyEnvRequestLabel(selectedRequestId, requests);
    const key = resolveApplyEnvKeySelection(selectedKey);
    const keyPhrase = key ? ` for key "${key}"` : ' (all matching keys)';
    const userText = buildApplyEnvToRequestUserMessage({
      envName,
      collectionLabel,
      requestLabel,
      keyPhrase,
    });
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const payload = buildApplyEnvToRequestPayload({
        projectId: selectedProjectId || undefined,
        collectionId: selectedCollectionId,
        requestId: selectedRequestId,
        environmentId: selectedEnvironmentId,
        key: key || undefined,
      });
      const res = await apiService.agentMessage({
        action: 'apply_environment_variables_to_request',
        chatId: chatId ?? undefined,
        payload,
      });
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (!res.error) {
        notifyAgentSideEffectsFromAgent(
          'apply_environment_variables_to_request',
          payload,
          res.state,
        );
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state!.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      await onAgentReply(res);
      onClose();
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingProjects || loadingDetails || loadingKeys;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
        ...environmentPlaceholderSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Apply environment variables to request
      </Typography>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-apply-env-req-project"
          label="Project"
          fullWidth
          value={selectedProjectId === '' ? '' : selectedProjectId}
          disabled={busy}
          emptyLabel="No projects found"
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-apply-env-req-collection"
          label="Collection"
          fullWidth
          value={selectedCollectionId === '' ? '' : selectedCollectionId}
          disabled={busy || !selectedProjectId}
          emptyLabel="No collections found"
          options={[
            { value: APPLY_ENV_SCOPE_ALL, label: 'All Collections' },
            ...collections.map((collection) => ({
              value: collection.id,
              label: collection.label,
              indent: collection.depth,
            })),
          ]}
          onChange={(raw) => {
            void handleCollectionChange(parseApplyEnvScopeSelectValue(raw));
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-apply-env-req-request"
          label="Request"
          fullWidth
          value={selectedRequestId === '' ? '' : selectedRequestId}
          disabled={busy || !selectedProjectId || selectedCollectionId === ''}
          emptyLabel="No requests found"
          maxListHeight={168}
          options={[
            { value: APPLY_ENV_SCOPE_ALL, label: 'All Requests' },
            ...requests.map((request) => ({
              value: request.id,
              label: request.name,
            })),
          ]}
          onChange={(raw) => {
            setSelectedRequestId(parseApplyEnvScopeSelectValue(raw));
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-apply-env-req-env"
          label="Environment"
          fullWidth
          value={selectedEnvironmentId === '' ? '' : selectedEnvironmentId}
          disabled={busy || !selectedProjectId}
          emptyLabel="No environments found"
          options={environments.map((env) => ({
            value: env.id,
            label: env.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            const environmentId = Number.isNaN(parsed) || raw === '' ? '' : parsed;
            setSelectedEnvironmentId(environmentId);
            void loadKeys(environmentId, defaultKey);
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-apply-env-req-key"
          label="Key"
          fullWidth
          value={selectedKey}
          disabled={busy || !selectedEnvironmentId}
          emptyLabel="No keys found"
          maxListHeight={168}
          options={[
            { value: APPLY_ENV_ALL_KEYS, label: 'All matching keys' },
            ...variableKeys.map((key) => ({ value: key, label: key })),
          ]}
          onChange={(raw) => setSelectedKey(raw)}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleApply()}
          disabled={
            busy ||
            !selectedEnvironmentId ||
            selectedCollectionId === '' ||
            selectedRequestId === ''
          }
          sx={{ textTransform: 'none' }}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : 'Apply'}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetApplyEnvToRequestForm;
